import i18n from "i18next";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import type { DocumentVersion } from "@/lib/api";
import { requestSaveVersion } from "@/lib/savedMoment/events";
import { PersonalityToggle } from "@/components/shared/PersonalityToggle";
import { testIds } from "@/testIds";
import type { SaveDisplay } from "./useSaveMoment";

const st = (key: string, opts?: Record<string, unknown>) => i18n.t(`editor:saveState.${key}`, opts);
const HEADLINE: Record<SaveDisplay, string> = {
  get saved() { return st("allSaved"); },
  get version() { return st("allSaved"); },
  get saving() { return st("saving"); },
  get offline() { return st("offline"); },
  get failed() { return st("failed"); },
  get new() { return st("new"); },
};

function formatTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const SAVE_VERSION_SHORTCUT = IS_MAC ? "⌘S" : "Ctrl+S";

export interface SaveIndicatorCardProps {
  display: SaveDisplay;
  documentId: string | null;
  lastSyncedAt: Date | null;
  pendingCount: number;
  detail: string | null;
  /** A version cut in this session; the list may not have refetched yet. */
  sessionVersion: { name: string; createdAt: string } | null;
  /** Viewers can read the card but cannot cut versions. */
  canEdit: boolean;
  onClose: () => void;
}

/**
 * The indicator's card (handoff §3): when and where it last saved, the last
 * named version, Save a version… ⌘S, History, and the Style: Pro | Fun
 * switch in its footer — where someone who just saw the moment will look.
 */
export function SaveIndicatorCard({
  display,
  documentId,
  lastSyncedAt,
  pendingCount,
  detail,
  sessionVersion,
  canEdit,
  onClose,
}: SaveIndicatorCardProps) {
  const { fetchWithAuth } = useApiContext();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);

  // The newest version a person named — not the machine's auto-saves.
  const { data: lastNamed } = useQuery({
    queryKey: ["document-versions", documentId, "last-named"],
    enabled: Boolean(documentId),
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fetchWithAuth(`${API_BASE}/documents/${documentId}/versions?limit=20`);
      const versions = await handleResponse<DocumentVersion[]>(res);
      return versions.find((v) => !v.isAutoSave) ?? null;
    },
  });

  useEffect(() => {
    if (naming) nameRef.current?.focus();
  }, [naming]);

  const shownVersion = sessionVersion
    ? sessionVersion
    : lastNamed
      ? { name: lastNamed.name || `Version ${lastNamed.versionNumber}`, createdAt: lastNamed.createdAt }
      : null;

  const where =
    display === "offline"
      ? pendingCount > 0
        ? st("willSync", { count: pendingCount })
        : st("everythingWillSync")
      : display === "failed"
        ? `${detail ? `${detail}. ` : ""}${st("onDevice")}`
        : display === "new"
          ? st("savesWhenType")
          : lastSyncedAt
            ? st("lastSave", { time: formatTime(lastSyncedAt) })
            : st("onServers");

  const submitVersion = () => {
    requestSaveVersion({ name });
    setName("");
    setNaming(false);
    onClose();
  };

  const openHistory = () => {
    // The history rail — EditorPage listens (same as the validate / outline rails).
    window.dispatchEvent(new CustomEvent("lilia:open-history-rail"));
    onClose();
  };

  return (
    <div data-testid={testIds.editor.saveIndicatorCard} data-state={display} style={{ fontFamily: "var(--lilia-font-sans)" }}>
      <div className="flex flex-col gap-[3px] px-4 pb-2.5 pt-3.5">
        <div className="text-[13px] font-semibold text-[var(--lilia-text)]">{HEADLINE[display]}</div>
        <div data-testid="save-indicator-card-where" className="text-[12px] leading-snug text-[var(--lilia-text-secondary)]">
          {where}
        </div>
        <div data-testid="save-indicator-card-version" className="text-[12px] text-[var(--lilia-text-secondary)]" style={{ fontVariantNumeric: "tabular-nums" }}>
          {shownVersion
            ? <>{st("lastVersion")} <span aria-hidden>◆</span> “{shownVersion.name}”, {formatTime(new Date(shownVersion.createdAt))}</>
            : st("noVersions")}
        </div>
      </div>

      {(canEdit || documentId) && (
        <div className="flex items-center gap-2.5 border-t border-[var(--lilia-border)] px-4 py-2">
          {canEdit && !naming && (
            <button
              type="button"
              data-testid={testIds.editor.saveIndicatorSaveVersion}
              onClick={() => setNaming(true)}
              className="flex flex-1 items-center gap-2 rounded text-left text-[12.5px] font-medium text-[var(--lilia-primary)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lilia-primary)]"
            >
              {st("saveVersion")}
              <kbd className="font-mono text-[11.5px] font-normal text-[var(--lilia-text-muted)]">{SAVE_VERSION_SHORTCUT}</kbd>
            </button>
          )}
          {canEdit && naming && (
            <form
              className="flex flex-1 items-center gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                submitVersion();
              }}
            >
              <input
                ref={nameRef}
                data-testid={testIds.editor.saveIndicatorVersionName}
                aria-label={st("versionName")}
                placeholder={st("nameIt")}
                value={name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  // Esc leaves the name field first; a second Esc closes the card.
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    setNaming(false);
                  }
                }}
                className="h-7 min-w-0 flex-1 rounded-md border border-[var(--lilia-border-strong)] bg-[var(--lilia-bg)] px-2 text-[12.5px] text-[var(--lilia-text)] outline-none focus:border-[var(--lilia-primary)]"
              />
              <button
                type="submit"
                data-testid={testIds.editor.saveIndicatorVersionSubmit}
                className="h-7 rounded-md bg-[var(--lilia-primary)] px-2.5 text-[12px] font-medium text-[color:var(--lilia-on-primary)] hover:opacity-90"
              >
                {st("save")}
              </button>
            </form>
          )}
          {!canEdit && <span className="flex-1" />}
          <button
            type="button"
            data-testid={testIds.editor.docVersionHistory}
            onClick={openHistory}
            className="rounded text-[12.5px] font-medium text-[var(--lilia-primary)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lilia-primary)]"
          >
            {st("history")}
          </button>
        </div>
      )}

      <div className="flex items-center gap-2.5 border-t border-[var(--lilia-border)] bg-[var(--lilia-bg-secondary)] px-4 py-2">
        <span className="flex-1 text-[12px] text-[var(--lilia-text-secondary)]">{st("style")}</span>
        <PersonalityToggle />
      </div>
    </div>
  );
}
